import { bool, func, object, shape, string } from 'prop-types';
import React, { Component } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { withRouter } from 'react-router-dom';
import _isEqual from 'lodash/isEqual';

import TextArea from 'ravenjs/lib/TextArea';
import { get, isEmpty } from 'ravenjs/utils/lodash';
import Typography from 'ravenjs/lib/Typography';
import { callFunc, getHandler } from 'ravenjs/utils/actions';
import Box from 'ravenjs/lib/Box';
import Input from 'ravenjs/lib/Input';
import Col from 'ravenjs/lib/Col';
import Button from 'ravenjs/lib/Button';
import Row from 'ravenjs/lib/Row';
import { genID } from 'ravenjs/utils/generate';
import { getThemeProps } from 'ravenjs/utils/theme';
import { renderCheck } from 'ravenjs/utils/viewport';
import Form from 'ravenjs/lib/Form';
import Link from 'ravenjs/lib/Link';

import { MODALS } from 'constants/modals';
import MESSAGES from 'constants/messages';

import { actions as uiActions, selectors as uiSelectors } from 'modules/ui';
import { actions as calendarActions } from 'modules/calendar';
import { selectors as userSelectors } from 'modules/user';

import { isValidUrl } from 'utils/common';
import { extractErrorMessage } from 'utils/errors';
import { isContentAuthor } from 'utils/user';
import { convertTimestampToUtc } from 'utils/date';

import { DateField, EditorField } from 'components/Form/fields';
import DetailsView from 'components/DetailsView';
import { TrashIcon } from 'components/Icons';
import Banner from 'components/Banner';

import {
    ADD_EVENT,
    ADD_GLOBAL_EVENT,
    ADD_URL,
    ADD_URLS,
    ERROR_MESSAGES,
    SPECIAL_NOTE,
    UPDATE_EVENT,
    UPDATE_GLOBAL_EVENT,
} from 'constants/calendar';
import { DEFAULT_COLORS } from 'constants/colors';

import Modal from '../Modal';

import eventFormSchema from './eventFormSchema.json';

const CalendarEventModalStyled = styled.div`
    position: relative;
    .form-group {
        [type='required'] {
            margin-top: 0 !important;
        }
    }
    .inputDataPicker {
        input {
            border-radius: 0px;
            border-color: ${DEFAULT_COLORS.PLACEHOLDER} !important;
        }
        &.endDate {
            input {
                margin-left: -1px;
            }
        }
    }
    .react-datepicker {
        .react-datepicker__navigation {
            &:focus {
                outline: none;
            }
        }
    }
`;

const NavTabs = styled.div`
    display: flex;
    flex-wrap: wrap;
    padding-left: 0;
    margin-bottom: 0;
    list-style: none;
`;

const NavTabsItem = styled.div`
    position: relative;
    padding: 0.5rem 0;
    margin: 0 0.5rem;
    cursor: pointer;
    border-bottom: 5px solid transparent;
    transition: all 0.25 ease;
    &.active {
        border-color: ${({ theme }) =>
            getThemeProps('NavItem.active.backgroundColor', DEFAULT_COLORS.BLUE, { theme })};
    }
    &:first-child {
        margin-left: 0;
    }
`;

const ListItemsAdd = styled.div`
    position: relative;
    display: flex;
    justify-content: space-between;
    padding: 0.75rem 0.75rem;
    align-items: center;
    background-color: ${DEFAULT_COLORS.WHITE};
    &:nth-child(odd) {
        background-color: ${DEFAULT_COLORS.LIGHTEST};
    }
`;

const ListItemsAddDetail = styled.div`
    position: relative;
`;

const ActionsModalInner = styled.div`
    position: relative;
    display: flex;
    justify-content: flex-end;
    margin: 1.5rem 0;
    button {
        margin: 0 0.5rem;
        &:last-child {
            margin: 0;
        }
    }
`;

class CalendarEventModal extends Component {
    constructor(props) {
        super(props);
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        this.state = {
            addUrl: true,
            errorMessage: '',
            event: { startDate: currentDate, endDate: currentDate },
            listUrls: [],
            removeResources: [],
            specialNote: '',
            currentTitle: '',
            currentUrl: '',
            addEventTitle: isContentAuthor(props.loggedUserRole) ? ADD_GLOBAL_EVENT : ADD_EVENT,
            updateEventTitle: isContentAuthor(props.loggedUserRole)
                ? UPDATE_GLOBAL_EVENT
                : UPDATE_EVENT,
        };

        this.currentTitleRef = React.createRef();
        this.currentUrlRef = React.createRef();
        this.eventRef = React.createRef();
    }

    componentDidUpdate = prevProps => {
        const { modal: prevModal } = prevProps;
        const { modal } = this.props;
        const currentData = get(modal, 'data.dataEvent', {});
        const prevData = get(prevModal, 'data.dataEvent', {});
        const { specialNote, listUrls } = this.state;
        const open = get(modal, 'open');

        if (
            !_isEqual(currentData, prevData) ||
            ((!_isEqual(prevData.specialNote, specialNote) ||
                !_isEqual(prevData.resources, listUrls)) &&
                !_isEqual(open, true))
        ) {
            this.updateEventInformation(currentData);
        }
    };

    updateEventInformation = info => {
        const { startDate, endDate, title, htmlDescription, resources, specialNote, type } = info;

        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        this.setState({
            addUrl: true,
            event: {
                startDate: startDate || currentDate,
                endDate: endDate || currentDate,
                title,
                description: htmlDescription,
            },
            listUrls: type !== 'add' ? resources : [],
            specialNote: type !== 'add' ? specialNote : '',
        });
    };

    getModalActions = () => {
        const { modal } = this.props;
        const { addEventTitle, event, updateEventTitle } = this.state;
        const handlers = get(modal, 'data.handlers', {});
        const handleDeleteEvent = getHandler(handlers, 'handleDeleteEvent');
        const id = get(modal, 'data.dataEvent.id', '');
        const currentEvent = { ...event, id, formUpdate: true };
        let deleteButton = null;

        if (this.isUpdate()) {
            deleteButton = {
                onClick: () => {
                    callFunc(handleDeleteEvent, currentEvent);
                },
                text: 'Delete',
                color: 'secondary',
            };
        }

        return [
            deleteButton,
            {
                onClick: this.externalHandleOnAdd,
                text: this.isUpdate() ? updateEventTitle : addEventTitle,
                color: 'primary',
            },
        ].filter(function(el) {
            return el != null;
        });
    };

    renderActions = () => {
        const actions = this.getModalActions();

        return (
            <ActionsModalInner>
                {actions.map(action => (
                    <Button
                        key={genID()}
                        color={action.color}
                        onClick={action.onClick}
                        disabled={this.state.errorMessage}
                    >
                        {action.text}
                    </Button>
                ))}
            </ActionsModalInner>
        );
    };

    isUpdate = () => {
        const { modal } = this.props;
        const type = get(modal, 'data.type');

        return type === 'update';
    };

    getModalProps = () => {
        const { modal } = this.props;
        const ModalProps = get(modal, 'data.ModalProps', {});
        const hasCloseIcon = get(modal, 'data.hasCloseIcon');
        const open = get(modal, 'open', false);

        return {
            actions: [],
            content: this.renderContent(),
            hideActions: true,
            id: MODALS.CALENDAR_EVENT_MODAL,
            ModalProps,
            modalHeaderProps: {
                display: 'flex',
                borderColor: 'transparent',
                backgroundColor: DEFAULT_COLORS.WHITE,
            },
            modalBodyProps: {
                padding: renderCheck('md', 'less') ? '0.9375rem' : '0 2.5rem 2.5rem 2.5rem',
                borderRadius: '0',
            },
            onClickCloseIcon: hasCloseIcon ? this.closeModal : null,
            open,
        };
    };

    closeModal = () => {
        const { closeModal, modal } = this.props;
        const onCloseIcon = get(modal, 'data.onCloseIcon');
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        this.setState({
            addUrl: true,
            errorMessage: '',
            event: { startDate: currentDate, endDate: currentDate },
            listUrls: [],
            removeResources: [],
            specialNote: '',
            currentTitle: '',
            currentUrl: '',
        });

        closeModal(MODALS.CALENDAR_EVENT_MODAL);

        if (this.isUpdate()) {
            callFunc(onCloseIcon);
        }
    };

    buildModalParams = () => {
        const { modal } = this.props;
        const id = get(modal, 'data.dataEvent.id', '');
        const { listUrls, removeResources, specialNote } = this.state;
        const formData = this.eventRef.current.getFormData();
        const resources = !isEmpty(listUrls) && listUrls.filter(option => isEmpty(option.id));

        return {
            params: {
                id,
                description: formData.description,
                endDate: Math.floor(new Date(formData.endDate).getTime() / 1000),
                removeResources,
                resources,
                specialNote,
                startDate: Math.floor(new Date(formData.startDate).getTime() / 1000),
                title: formData.title,
            },
        };
    };

    externalHandleOnAdd = () => {
        this.eventRef.current && this.eventRef.current.submitButton.click();
    };

    handleOnSubmit = async () => {
        const {
            createCalendarEvent,
            isLoading,
            modal,
            openModal,
            updateCalendarEvent,
        } = this.props;
        const reloadCalendar = get(modal, 'data.reloadCalendar');
        const onSuccess = get(modal, 'data.onSuccess');
        const apiRequest = this.isUpdate() ? updateCalendarEvent : createCalendarEvent;
        isLoading(true, MESSAGES.LOADING.DEFAULT);

        try {
            const response = await apiRequest(this.buildModalParams());
            this.closeModal();

            if (!this.isUpdate()) {
                callFunc(reloadCalendar);
            } else {
                callFunc(onSuccess, response.results);
            }
        } catch (e) {
            const error = extractErrorMessage(e);
            openModal(MODALS.CONFIRM, {
                title: MESSAGES.MODAL.CONFIRM.TITLE.ERROR,
                description: error,
            });
        } finally {
            isLoading(false);
        }
    };

    handleFormChange = ({ formData }) => {
        this.setState(() => ({ event: formData }));
    };

    toggleTab = () => {
        const { addUrl } = this.state;

        this.setState({ addUrl: !addUrl });
    };

    addNewUrl = () => {
        let errorMessage = '';

        if (isEmpty(this.currentTitleRef.current.value)) {
            errorMessage = ERROR_MESSAGES.EMPTY_TITLE;
        } else if (!isValidUrl(this.currentUrlRef.current.value)) {
            errorMessage = ERROR_MESSAGES.VALID_URL;
        } else if (this.currentTitleRef.current.value && this.currentUrlRef.current.value) {
            const { listUrls = [] } = this.state;
            const urls = [
                ...listUrls,
                {
                    title: this.currentTitleRef.current.value,
                    url: this.currentUrlRef.current.value,
                },
            ];

            this.setState({ listUrls: urls, currentTitle: '', currentUrl: '' }, () => {
                this.currentTitleRef.current.value = '';
                this.currentUrlRef.current.value = '';
            });
        }

        this.setState({ errorMessage });
    };

    removeUrl = option => {
        const { modal } = this.props;
        const { listUrls, removeResources } = this.state;
        const id = get(modal, 'data.dataEvent.id', '');
        const urls = !isEmpty(listUrls) && listUrls.filter(item => !_isEqual(item, option));

        if (id && option.id) {
            this.setState({ removeResources: [option.id, ...removeResources] });
        }

        this.setState({ listUrls: urls });
    };

    validateUrl = () => {
        if (
            isValidUrl(this.currentUrlRef.current.value) ||
            (isEmpty(this.currentTitleRef.current.value) &&
                isEmpty(this.currentUrlRef.current.value))
        ) {
            this.setState({ errorMessage: '' });
        }

        this.setState({
            currentTitle: this.currentTitleRef.current.value,
            currentUrl: this.currentUrlRef.current.value,
        });
    };

    renderUrls = () => {
        const { errorMessage, listUrls } = this.state;

        return (
            <>
                <Row>
                    {!isEmpty(errorMessage) && (
                        <Col size={12}>
                            <Banner
                                alignItems="center"
                                color={DEFAULT_COLORS.LIGHTER}
                                displayText={errorMessage}
                                dismissible={false}
                                onlyText
                                height="auto"
                                padding="0.5rem"
                                margin="0 0 1rem 0"
                            />
                        </Col>
                    )}
                    <Col size={{ xs: 12, md: 4, lg: 5 }}>
                        <Typography
                            gutterTop={renderCheck('md', 'less') ? '0.5rem' : '0'}
                            type="labelInputs"
                        >
                            {ADD_URLS.URL_TITLE}
                        </Typography>
                        <Input
                            type="text"
                            ref={this.currentTitleRef}
                            defaultValue={this.state.currentTitle}
                            onChange={this.validateUrl}
                        />
                    </Col>
                    <Col size={{ xs: 12, md: 4, lg: 5 }}>
                        <Typography
                            gutterTop={renderCheck('md', 'less') ? '1rem' : '0'}
                            type="labelInputs"
                        >
                            {ADD_URLS.URL}
                        </Typography>
                        <Input
                            type="text"
                            ref={this.currentUrlRef}
                            defaultValue={this.state.currentUrl}
                            onChange={this.validateUrl}
                        />
                    </Col>
                    <Col size={{ xs: 12, md: 2, lg: 2 }}>
                        <Button
                            color="yellow"
                            noMinWidth
                            minHeight="2.375rem !important"
                            margin={renderCheck('md', 'less') ? '1rem 0 0 0' : '1.5rem 0 0 0'}
                            width="100%"
                            onClick={this.addNewUrl}
                        >
                            {ADD_URLS.ADD}
                        </Button>
                    </Col>
                </Row>
                {!isEmpty(listUrls) && (
                    <Box
                        padding="0"
                        margin="1rem 0 0 0"
                        borderWidth="0"
                        backgroundColor="transparent"
                    >
                        {listUrls.map(item => {
                            return (
                                <ListItemsAdd key={genID()}>
                                    <ListItemsAddDetail>
                                        <Link
                                            type="paragraph"
                                            gutterBottom="0 !important"
                                            href={item.url}
                                        >
                                            {item.title}
                                        </Link>
                                        <Typography
                                            gutterTop="0.5"
                                            gutterBottom="0 !important"
                                            type="paragraph"
                                            fontWeight="normal"
                                            fontSize="0.6875rem"
                                            color={`${DEFAULT_COLORS.GREY_3} !important`}
                                        >
                                            {item.url}
                                        </Typography>
                                    </ListItemsAddDetail>
                                    <Typography
                                        gutterTop="0"
                                        gutterBottom="0"
                                        type="span"
                                        style={{
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => {
                                            this.removeUrl(item);
                                        }}
                                    >
                                        <TrashIcon width="14" height="14" />
                                    </Typography>
                                </ListItemsAdd>
                            );
                        })}
                    </Box>
                )}
            </>
        );
    };

    renderSpecialNote = () => {
        const { specialNote } = this.state;

        return (
            <TextArea
                id="text-area"
                height="15rem"
                padding="0.875rem"
                value={specialNote}
                onChange={e => this.setState({ specialNote: e.target.value })}
            />
        );
    };

    buildFormData = data => {
        return {
            description: data.htmlDescription,
            endDate: convertTimestampToUtc(data.endDate),
            startDate: convertTimestampToUtc(data.startDate),
            title: data.title,
        };
    };

    renderEventForm = () => {
        const { modal } = this.props;
        const { addEventTitle, addUrl, event, updateEventTitle } = this.state;
        const dataEvent = get(modal, 'data.dataEvent', {});
        const title = this.isUpdate() ? updateEventTitle : addEventTitle;

        return (
            <>
                <Typography type="modalTitle">{title}</Typography>
                <DetailsView col={{ largeSize: 12, padding: 0 }}>
                    <Form
                        {...eventFormSchema}
                        enableDirtyCheck
                        externalSubmission
                        fields={{ DateField, EditorField }}
                        formData={!isEmpty(dataEvent) ? this.buildFormData(dataEvent) : event}
                        liveValidate
                        ref={this.eventRef}
                        onSubmit={this.handleOnSubmit}
                        showErrorList={false}
                    />
                </DetailsView>
                <NavTabs>
                    <NavTabsItem onClick={this.toggleTab} className={addUrl ? 'active' : ''}>
                        <Typography
                            type="paragraph"
                            fontSize="0.875rem"
                            fontWeight="normal"
                            gutterTop="0"
                            gutterBottom="0 !important"
                        >
                            {ADD_URL}
                        </Typography>
                    </NavTabsItem>
                    <NavTabsItem onClick={this.toggleTab} className={!addUrl ? 'active' : ''}>
                        <Typography
                            type="paragraph"
                            fontSize="0.875rem"
                            fontWeight="normal"
                            gutterTop="0"
                            gutterBottom="0 !important"
                        >
                            {SPECIAL_NOTE}
                        </Typography>
                    </NavTabsItem>
                </NavTabs>
                <Box margin="1rem 0 0 0" padding="0" borderWidth="0" backgroundColor="transparent">
                    {addUrl ? this.renderUrls() : this.renderSpecialNote()}
                    {this.renderActions()}
                </Box>
            </>
        );
    };

    renderContent = () => {
        return <CalendarEventModalStyled>{this.renderEventForm()}</CalendarEventModalStyled>;
    };

    render() {
        return <Modal {...this.getModalProps()} />;
    }
}

CalendarEventModal.propTypes = {
    closeModal: func.isRequired,
    createCalendarEvent: func.isRequired,
    deleteCalendarEvent: func.isRequired,
    isLoading: func.isRequired,
    loggedUserRole: string.isRequired,
    modal: shape({
        data: object,
        open: bool,
    }),
    openModal: func.isRequired,
    updateCalendarEvent: func.isRequired,
};

CalendarEventModal.defaultProps = {
    modal: {
        data: {},
        open: false,
    },
};

const mapStateToProps = createStructuredSelector({
    loggedUserRole: userSelectors.getUserRole,
    modal: uiSelectors.getModalById(MODALS.CALENDAR_EVENT_MODAL),
});

const mapDispatchToProps = {
    closeModal: uiActions.closeModal,
    deleteCalendarEvent: calendarActions.deleteCalendarEvent,
    openModal: uiActions.openModal,
    createCalendarEvent: calendarActions.createCalendarEvent,
    isLoading: uiActions.isLoading,
    updateCalendarEvent: calendarActions.updateCalendarEvent,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CalendarEventModal));
